import { pageIntro } from '../shared/components/Narrative';

// Generic hub page (nav consolidation 2026-06-27). A single sidebar entry that
// holds several related pages as internal tabs — pares ~25 sidebar items down to
// a handful of hubs. Each tab's `mount` renders an existing page into the host;
// the host's CSS suppresses the child's own page-intro/padding so it nests clean.
// mount may return the mounted page instance — the hub uses it to delegate
// suspendAutoRefresh (R37), so a capture in progress inside a hub tab isn't
// wiped by the 15-min timer. Returning nothing is fine.
// roles (optional) only drives the LANDING tab — a role that can't open tabs[0]
// lands on the first tab it can (R105 #6: remote_ops reaching Resolutions inside
// Accounting); the mount gates stay the enforcement.
export interface HubTab { id: string; label: string; roles?: string[]; mount: (host: HTMLElement) => Promise<unknown> | unknown; }
// R105 #6: stale one-shot hub_tab ids that moved to another page/hub keep landing.
export type HubForward = Record<string, { page: string; tab?: string }>;
export interface HubMeta { icon: string; title: string; purpose: string; }

export class HubPage {
  private container: HTMLElement;
  private meta: HubMeta;
  private tabs: HubTab[];
  private active: string;
  private rendering = false;
  private forward: HubForward;

  constructor(container: HTMLElement, meta: HubMeta, tabs: HubTab[], forward?: HubForward) {
    this.container = container; this.meta = meta; this.tabs = tabs; this.forward = forward || {};
    this.active = this.landingTab();
  }
  private role(): string { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } }
  // First tab the role may open (tabs without roles are open to all); tabs[0] as the last resort.
  private landingTab(): string {
    const r = this.role();
    const t = this.tabs.find(t => !t.roles || !r || t.roles.includes(r)) || this.tabs[0];
    return t ? t.id : '';
  }

  // Deep-link / role-landing into a specific sub-tab.
  setTab(id: string): void { if (this.tabs.some(t => t.id === id)) this.active = id; }

  async render(): Promise<void> {
    // One-shot tab preselect for cross-hub deep links: a page sets
    // sessionStorage 'hub_tab' then navigates to the hub.
    try {
      const pre = sessionStorage.getItem('hub_tab');
      if (pre) {
        sessionStorage.removeItem('hub_tab');
        const fwd = this.forward[pre];
        if (fwd && !this.tabs.some(t => t.id === pre)) {   // R105 #6: the tab moved — hand off like App's redirect()
          if (fwd.tab) sessionStorage.setItem('hub_tab', fwd.tab);
          document.dispatchEvent(new CustomEvent('app:navigate', { detail: fwd.page }));
          return;
        }
        this.setTab(pre);
      }
    } catch { /* sessionStorage unavailable */ }
    this.container.innerHTML = `
      <div class="page-content">
        <style>
          .hub-tabs { display:flex; gap:8px; margin-bottom:16px; flex-wrap:wrap; }
          .hub-tab { padding:8px 16px; border-radius:var(--radius-pill); font-size:13px; font-weight:500; cursor:pointer; border:1px solid var(--border); background:var(--card); color:var(--text-muted); box-shadow:var(--shadow-card); transition:background .14s ease, color .14s ease, border-color .14s ease; }
          .hub-tab:hover { border-color:var(--accent-border); color:var(--text); }
          /* R110 #1: the active tab is the HostBuddy nav pill — pale blue, blue text */
          .hub-tab.active { font-weight:600; border-color:var(--accent-border); background:var(--accent-soft); color:var(--accent); }
          /* R115 mobile: on a phone the tabs are ONE scrolling line (six tabs
             wrapped to two rows of pills); the active tab is scrolled into view. */
          @media (max-width: 640px) {
            .hub-tabs { flex-wrap:nowrap; overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none; gap:6px; margin-bottom:12px; padding:2px 0 4px; }
            .hub-tabs::-webkit-scrollbar { display:none; }
            .hub-tab { flex:none; white-space:nowrap; padding:8px 14px; }
          }
          /* Child pages bring their own intro + padding — suppress both so they nest. */
          #hub-host > .page-content { padding:0; }
          #hub-host .page-intro { display:none; }
        </style>
        ${pageIntro({ icon: this.meta.icon, title: this.meta.title, purpose: this.meta.purpose })}
        <div class="hub-tabs" id="hub-tabs">${this.tabs.map(t => `<button class="hub-tab${t.id === this.active ? ' active' : ''}" data-hub-tab="${t.id}">${t.label}</button>`).join('')}</div>
        <div id="hub-host"></div>
      </div>`;

    document.getElementById('hub-tabs')?.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-hub-tab]');
      if (!btn || this.rendering) return;
      this.active = btn.dataset.hubTab!;
      document.querySelectorAll('.hub-tab').forEach(b => b.classList.toggle('active', (b as HTMLElement).dataset.hubTab === this.active));
      this.revealActiveTab();
      this.mount();
    });
    this.revealActiveTab();

    await this.mount();
  }

  // R115 mobile: keep the active tab visible inside the phone tab scroller
  // (scrolls the strip only — never the page).
  private revealActiveTab(): void {
    const strip = document.getElementById('hub-tabs');
    const act = strip?.querySelector<HTMLElement>('.hub-tab.active');
    if (!strip || !act || strip.scrollWidth <= strip.clientWidth) return;
    const l = act.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
    if (l < strip.scrollLeft || l + act.offsetWidth > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = Math.max(0, l - 16);
  }

  // R37: the page instance the active tab mounted (when its mount returns it) —
  // lets App's auto-refresh veto reach the instance actually rendering inside
  // #hub-host instead of the unused singleton.
  private mountedPage: any = null;
  suspendAutoRefresh(): boolean {
    const p = this.mountedPage;
    return !!(p && typeof p.suspendAutoRefresh === 'function' && p.suspendAutoRefresh());
  }

  private async mount(): Promise<void> {
    const host = document.getElementById('hub-host');
    if (!host) return;
    this.rendering = true;
    this.mountedPage = null;
    try {
      host.innerHTML = `<div style="padding:36px; text-align:center; color:var(--text-muted);">Loading…</div>`;
      const tab = this.tabs.find(t => t.id === this.active);
      host.innerHTML = '';
      if (tab) this.mountedPage = (await tab.mount(host)) || null;
    } catch (e) {
      host.innerHTML = `<div style="padding:36px; text-align:center; color:var(--danger);">This view failed to load.</div>`;
      console.error('[hub] mount failed', e);
    } finally {
      this.rendering = false;
    }
  }
}
